<template>
  <AppShell :scrollable="session.screen === 'report'">
    <Transition name="screen-fade" mode="out-in">
      <StartScreen v-if="session.screen === 'start'" key="start" @start="session.startPretest" />

      <IntroScreen
        v-else-if="session.screen === 'pretest-intro'"
        key="pretest-intro"
        eyebrow="Pretest"
        title="먼저 현재 수학 학습 수준을 확인합니다."
        :body="`간단한 ${contentStore.activePretest.length}개의 문제에 답해 주세요.`"
        button-text="시작하기"
        icon="◎"
        @continue="session.beginPretest"
      />

      <PretestQuestion
        v-else-if="session.screen === 'pretest' && currentPretest"
        :key="currentPretest.id"
        :question="currentPretest"
        :current="session.pretestIndex + 1"
        :total="contentStore.activePretest.length"
        @answered="handlePretestAnswer"
        @close="confirmRestart"
      />

      <LevelResultScreen
        v-else-if="session.screen === 'level-result'"
        key="level-result"
        @continue="goToMainIntro"
      />

      <IntroScreen
        v-else-if="session.screen === 'main-intro'"
        key="main-intro"
        eyebrow="Main Test"
        title="이제 문제를 푸는 방식과 오류 판단 능력을 진단합니다."
        body="정답만이 아니라 어떤 방식으로 생각하고 검토하는지를 살펴봅니다."
        button-text="진단 시작하기"
        icon="✦"
        :items="mainIntroItems"
        @continue="session.beginMain"
      />

      <component
        :is="currentMainComponent"
        v-else-if="session.screen === 'main' && currentMain"
        :key="currentMain.id"
        :question="currentMain"
        :current="session.mainIndex + 1"
        :total="contentStore.activeMainTest.length"
        @answered="handleMainAnswer"
        @close="confirmRestart"
      />

      <AnalyzingScreen v-else-if="session.screen === 'analyzing'" key="analyzing" />

      <ReportScreen
        v-else-if="session.screen === 'report'"
        key="report"
        :report="fixedReport"
        @restart="restart"
      />
    </Transition>
  </AppShell>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue'
import AppShell from '@/components/AppShell.vue'
import AnalyzingScreen from '@/screens/AnalyzingScreen.vue'
import IntroScreen from '@/screens/IntroScreen.vue'
import LevelResultScreen from '@/screens/LevelResultScreen.vue'
import ReportScreen from '@/screens/ReportScreen.vue'
import StartScreen from '@/screens/StartScreen.vue'
import PretestQuestion from '@/question-types/PretestQuestion.vue'
import TypeAQuestion from '@/question-types/TypeAQuestion.vue'
import TypeBQuestion from '@/question-types/TypeBQuestion.vue'
import TypeCQuestion from '@/question-types/TypeCQuestion.vue'
import { fixedReport } from '@/data/fixed-report'
import { useContentStore } from '@/stores/content'
import { useTestSessionStore } from '@/stores/testSession'

const contentStore = useContentStore()
const session = useTestSessionStore()
let levelTimer: number | null = null
let analyzingTimer: number | null = null

const currentPretest = computed(() => contentStore.activePretest[session.pretestIndex])
const currentMain = computed(() => contentStore.activeMainTest[session.mainIndex])
const currentMainComponent = computed(() => {
  if (!currentMain.value) return TypeAQuestion
  if (currentMain.value.type === 'B') return TypeBQuestion
  if (currentMain.value.type === 'C') return TypeCQuestion
  return TypeAQuestion
})

const mainIntroItems = [
  { title: 'A형', text: '떠올린 풀이 방법 선택' },
  { title: 'B형', text: '풀이에서 오류가 시작된 부분 찾기' },
  { title: 'C형', text: '문제 자체의 오류 여부 판단' },
]

onMounted(() => {
  contentStore.loadFromBrowser()
})

onBeforeUnmount(() => {
  if (levelTimer !== null) window.clearTimeout(levelTimer)
  if (analyzingTimer !== null) window.clearTimeout(analyzingTimer)
})

function handlePretestAnswer(payload: { choiceId: string; selectedByUser: boolean; elapsedMs: number }): void {
  if (!currentPretest.value) return
  session.recordAnswer({
    questionId: currentPretest.value.id,
    selectedChoiceId: payload.choiceId,
    selectedByUser: payload.selectedByUser,
    reason: payload.selectedByUser ? 'user' : 'timeout',
    elapsedMs: payload.elapsedMs,
  })

  if (session.pretestIndex + 1 < contentStore.activePretest.length) {
    session.pretestIndex += 1
    return
  }
  session.showLevel()
}

function goToMainIntro(): void {
  if (levelTimer !== null) {
    window.clearTimeout(levelTimer)
    levelTimer = null
  }
  session.showMainIntro()
}

function handleMainAnswer(payload: { choiceId: string; selectedByUser: boolean; elapsedMs: number }): void {
  if (!currentMain.value) return
  session.recordAnswer({
    questionId: currentMain.value.id,
    selectedChoiceId: payload.choiceId,
    selectedByUser: payload.selectedByUser,
    reason: payload.selectedByUser ? 'user' : 'timeout',
    elapsedMs: payload.elapsedMs,
  })

  if (session.mainIndex + 1 < contentStore.activeMainTest.length) {
    session.mainIndex += 1
    return
  }

  session.showAnalyzing()
  analyzingTimer = window.setTimeout(() => session.showReport(), 1300)
}

function confirmRestart(): void {
  const shouldRestart = window.confirm('테스트를 종료하고 처음 화면으로 돌아갈까요?')
  if (shouldRestart) restart()
}

function restart(): void {
  if (levelTimer !== null) window.clearTimeout(levelTimer)
  if (analyzingTimer !== null) window.clearTimeout(analyzingTimer)
  levelTimer = null
  analyzingTimer = null
  session.reset()
}
</script>
